import { useRef, useState } from "react";

import { useLightbox } from "@/components/shell/Lightbox";
import { mediaUrl } from "@/lib/note";

import { firstImage } from "./mediaSlots";

/**
 * The two image slots a sentence owns.
 *
 * PRD §6: **every card mined from that sentence inherits both slots plus the sentence text.** The
 * design calls this the main labour-saver in the whole redesign, which is why the slots live on the
 * sentence rather than on the card.
 *
 * A filled slot shows the actual screenshot, not a placeholder — you paste two similar frames from
 * the same scene and the only way to tell which landed where is to see them.
 */
export interface ImageSlot {
  /** The Anki field value, e.g. `<img src="vn-1.png">`. Empty means the slot is free. */
  value: string;
  filename: string | null;
}

export type SlotKey = "image" | "extraImage";

/**
 * The MIME type a slot-to-slot drag travels under.
 *
 * A private type, not `text/plain`: a drop handler has to be able to tell "an image from another
 * slot" from "a file from the desktop" from "some text", and only the first of those means move.
 * Anything unrecognised falls through to the existing file path unchanged.
 */
export const SLOT_DRAG_TYPE = "application/x-nihongonator-slot";

interface ImageSlotsProps {
  image: ImageSlot;
  extraImage: ImageSlot;
  /** Omitted for a past capture, which shows thumbnails rather than drop targets. */
  onFile?: (which: SlotKey, file: File) => void;
  onClear?: (which: SlotKey) => void;
  /**
   * Serialize this slot for a drag, or return null to make it undraggable.
   *
   * Separate from `onFile` on purpose: a **past** capture's slots are not editable — they are not
   * drop targets and never gain one — but they are still perfectly good drag *sources*. Tying the
   * two together would mean the only image you could move is the one already on the sentence you
   * are looking at.
   */
  onSlotDragStart?: (which: SlotKey) => string | null;
  /** Handle an image dropped from another slot. Only offered where `onFile` is. */
  onSlotDrop?: (which: SlotKey, payload: string) => void;
  /** Which slot is mid-upload, so the control can say so rather than look inert. */
  busy?: SlotKey | null;
  compact?: boolean;
  /** Phones get the touch hint; a `Ctrl+V` glyph there names a key that does not exist. */
  isPhone?: boolean;
}

const SLOTS = [
  { key: "image" as const, label: "画像", hint: "Ctrl+V", touchHint: "タップで追加" },
  {
    key: "extraImage" as const,
    label: "追加画像",
    hint: "Ctrl+Shift+Vまたはドロップ",
    touchHint: "タップで追加",
  },
];

export function ImageSlots({
  image,
  extraImage,
  onFile,
  onClear,
  onSlotDragStart,
  onSlotDrop,
  busy = null,
  compact = false,
  isPhone = false,
}: ImageSlotsProps) {
  const values = { image, extraImage };
  const width = `var(${compact ? "--slot-w-compact" : "--slot-w"})`;
  const height = `var(${compact ? "--slot-h-compact" : "--slot-h"})`;
  const [dropTarget, setDropTarget] = useState<SlotKey | null>(null);
  const inputs = useRef<Partial<Record<SlotKey, HTMLInputElement | null>>>({});
  const lightbox = useLightbox();

  return (
    <div className="flex items-start gap-[10px]">
      {SLOTS.map((slot) => {
        const { filename } = values[slot.key];
        const filled = filename !== null;
        const editable = onFile !== undefined;
        const isBusy = busy === slot.key;
        const isDropTarget = dropTarget === slot.key;
        // On a filled slot the desktop hint still names the paste target, which is what the design
        // marks the corner with. The touch hint has to change though: a filled slot no longer
        // takes a tap to add, it takes one to expand.
        const hint = isPhone ? (filled ? "タップで拡大" : slot.touchHint) : slot.hint;

        return (
          <div key={slot.key} className="flex flex-col gap-1">
            {!compact && (
              <span className="text-field-label font-semibold uppercase tracking-[.06em] text-ink-4">
                {slot.label}
              </span>
            )}
            <div className="relative">
              <button
                type="button"
                // A filled slot is clickable even when it is not editable — that is the whole point
                // of a past capture's thumbnail. Only an empty slot with nowhere to send the click
                // is inert.
                disabled={isBusy || (!filled && !editable)}
                onClick={() => {
                  // Filled: expand. The click used to re-open the file picker on an image you
                  // could already see, which is the one thing you never want from it. Replacing is
                  // still one gesture — drop a new file straight onto the slot — or clear and pick.
                  if (filled && filename) lightbox.open(filename);
                  else inputs.current[slot.key]?.click();
                }}
                onDragOver={(e) => {
                  if (!editable) return;
                  e.preventDefault();
                  setDropTarget(slot.key);
                }}
                onDragLeave={() => setDropTarget(null)}
                onDrop={(e) => {
                  if (!editable) return;
                  e.preventDefault();
                  setDropTarget(null);
                  // A slot payload wins over the file list, because a drag between slots carries
                  // BOTH: the browser synthesises no files, but a drag that started on an <img>
                  // still offers a URL. Checking files first would turn a move into a re-download.
                  const payload = e.dataTransfer.getData(SLOT_DRAG_TYPE);
                  if (payload && onSlotDrop) {
                    onSlotDrop(slot.key, payload);
                    return;
                  }
                  const file = firstImage(e.dataTransfer.files);
                  if (file) onFile(slot.key, file);
                }}
                // No `toLowerCase()` — Japanese has no case, and calling it on a label that is now
                // `追加画像` is a no-op that reads as if it were doing something.
                aria-label={filled ? `${slot.label}を拡大: ${filename}` : `${slot.label}を追加`}
                title={
                  filled
                    ? `${filename} — クリックで拡大${editable ? `、ファイルをドロップで差し替え` : ""}`
                    : `クリック・ドロップ・${slot.hint}で追加`
                }
                className="relative block overflow-hidden rounded-[var(--r-2)]"
                style={{
                  width,
                  height,
                  border: isDropTarget
                    ? "1.5px solid var(--accent)"
                    : filled
                      ? "1px solid var(--line-strong)"
                      : "1.5px dashed var(--line-strong)",
                  background: isDropTarget ? "var(--accent-tint)" : "var(--void)",
                  cursor: filled ? "zoom-in" : editable ? "pointer" : "default",
                }}
              >
                {filled && filename ? (
                  <>
                    <img
                      src={mediaUrl(filename)}
                      alt=""
                      // **Both are 日本-602, and both are about the reading log rather than this
                      // component.** A log of Alex's own carries a screenshot on nearly every one
                      // of 1,937 sentences, so opening it fetched and decoded 1,937 full-frame
                      // captures — a 1920x1128 picture shown in a 74x42 box. Measured by aborting
                      // `/api/captures/**`: 285 MB of browser memory and 1,937 HTTP round trips.
                      //
                      // Safe without any reflow because the slot carries an explicit `width` and
                      // `height` above, so a row's measured pitch never depended on the image
                      // having arrived — which is what the windowing needs to stay true.
                      loading="lazy"
                      decoding="async"
                      className="h-full w-full object-cover"
                      // Draggable only where a payload can be built. It used to be flatly false,
                      // to stop a drag starting when the gesture meant nothing.
                      draggable={onSlotDragStart !== undefined}
                      onDragStart={(e) => {
                        const payload = onSlotDragStart?.(slot.key);
                        if (!payload) {
                          e.preventDefault();
                          return;
                        }
                        e.dataTransfer.setData(SLOT_DRAG_TYPE, payload);
                        // `move` is the honest cursor for the swap case and a small lie for the
                        // copy case; `copy` would be the reverse. Swap is the one where getting it
                        // wrong loses track of an image, so the cursor matches that.
                        e.dataTransfer.effectAllowed = "move";
                      }}
                    />
                    {!compact && (
                      <span className="absolute -right-px -top-px rounded-bl-[var(--r-2)] rounded-tr-[var(--r-2)] bg-line-soft px-[5px] text-[14px]/[1.4] font-semibold text-ink-3">
                        {hint}
                      </span>
                    )}
                  </>
                ) : (
                  !compact && (
                    <span className="flex h-full flex-col items-center justify-center gap-0.5">
                      <span className="text-[14px] font-semibold text-ink-4">
                        {isBusy ? "保存中…" : "空"}
                      </span>
                      <span className="font-mono text-[14px] text-ink-4">{hint}</span>
                    </span>
                  )
                )}
              </button>

              {/* Clearing is a separate target so it cannot be hit while aiming at the slot. */}
              {filled && editable && !compact && onClear && (
                <button
                  type="button"
                  onClick={() => onClear(slot.key)}
                  aria-label={`${slot.label}を消去`}
                  title="消去"
                  className="absolute -left-px -top-px rounded-br-[var(--r-2)] rounded-tl-[var(--r-2)] bg-panel/90 px-[5px] text-[14px]/[1.6] font-semibold text-ink-3 hover:text-accent-text"
                >
                  ×
                </button>
              )}

              {editable && (
                <input
                  ref={(el) => {
                    inputs.current[slot.key] = el;
                  }}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => {
                    const file = firstImage(e.currentTarget.files);
                    if (file) onFile(slot.key, file);
                    // Reset, so choosing the same file twice still fires a change.
                    e.currentTarget.value = "";
                  }}
                />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
